import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LicenceCheckingManualCheckModal } from "pages/licence-checking/shared/manual-check/LicenceCheckingManualCheckModal";
import { FormLocationSelector, FormTextInput } from "components/form-fields";
import { Button } from "components/buttons";
import { Filter, FilterButton } from "components/filtering";
import { useSelector, useDispatch } from "react-redux";
import { ListingTable } from "components/listing";
import { EmployeeService, LicenceCheckingService } from "services";
import {
    NotificationInline,
    NotificationToaster,
} from "components/notifications";
import { Intent } from "@blueprintjs/core";
import { DateToLocal } from "components/dates";
import { UserAccess, UserHasAccess } from "components/user-access";
import { EmployeeStatus } from "components/status";
import { useDebounce } from "hooks/useDebounce";
import { Dropdown } from "components/dropdown";
import classNames from "classnames";
import axios from "axios";
import { InstantReportButton } from "components/reporting";
import { useQuery } from "hooks/useQuery";

export function LicenceCheckingCompletedChecks() {
    // General
    const requiredAction = "LicenceChecking:View";
    const licenceCheckManualCheckAction = "LicenceChecking:PerformCheck";
    const canAccessPage = UserHasAccess(requiredAction);
    const [isLoading, setIsLoading] = useState(true);
    const [initalSetupComplete, setInitalSetupComplete] = useState(false);
    // Search
    const query = useQuery();
    const queryFilter = query.get("filter");
    const debounceTimeout = 750;
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    // Location selector
    const reduxLocations = useSelector(
        (state) => state.licenceChecking.locations
    );
    const dispatch = useDispatch();
    const [useHierarchy, setUseHierarchy] = useState(true);
    const [useLocationDefaults, setUseLocationDefaults] = useState(null);
    // Filters
    const driversAgeKeys = [
        "DRIVERSUNDER21YEARSOFAGE",
        "DRIVERSOVER45YEARSOFAGE",
        "DRIVERSOVER65YEARSOFAGE",
    ];
    const pointsKeys = ["DRIVERSWITHPOINTS"];
    const enrolmentStatusKeys = [
        "DRIVERSENROLLED",
        "DRIVERSENROLLEDWITHOUTACHECK",
    ];

    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [pageFilters, setPageFilters] = useState([]);
    const [defaultFilterValues, setDefaultFilterValues] = useState([]);
    // Listing
    const tableHeaders = [
        "Employee Name",
        "Location",
        "Employee Category",
        "Age",
        "Photocard Expiry Date",
        "Short Term Licence",
        "Points",
        "Entitlement Expiry Date",
        "Policies Included",
        "Last Check Date",
        "Next Check Date",
        "Declaration Expiry Date",
        "Renewal Available Date",
        "Licence Status",
        "Check",
    ];
    const sortableHeaders = [
        { headerName: "Employee Name", sortName: "EMPLOYEE.FULLNAME" },
        { headerName: "Location", sortName: "EMPLOYEE.LOCATION" },
        { headerName: "Points", sortName: "POINTS" },
        {
            headerName: "Photocard Expiry Date",
            sortName: "PHOTOCARDEXPIRYDATE",
        },
        { headerName: "Last Check Date", sortName: "LASTCHECKDATE" },
        { headerName: "Next Check Date", sortName: "NEXTCHECKDATE" },
        { headerName: "Declaration Expiry Date", sortName: "CONSENTEXPIRYDATE" },
        { headerName: "Renewal Available Date", sortName: "RENEWALAVAILABLEDATE" },
    ];
    const [totalRecords, setTotalRecords] = useState(0);
    const [tableRows, setTableRows] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [sortedBy, setSortedBy] = useState(sortableHeaders[0]);
    const [sortedDir, setSortedDir] = useState("A");

    const currentPageNumber = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentLocations = useRef();
    const currentSearchTerm = useRef();
    //Manual check Modal
    const [showManualCheckModal, setShowManualCheckModal] = useState(false);
    const credits = useSelector((state) => state.licenceChecking.credits);
    const [manualCheckEmployeeId, setManualCheckEmployeeId] = useState("");
    const [manualCheckEmployeeName, setManualCheckEmployeeName] = useState("");
    // Reports
    const [showReportList, setShowReportList] = useState(false);
    const [reportList, setReportList] = useState([]);
    const [reportGenerationId, setReportGenerationId] = useState(null);
    // Options
    const [employeeTypeOptions, setEmployeeTypeOptions] = useState([]);
    const [employeeStatusOptions, setEmployeeStatusOptions] = useState([]);
    const [checkFiltersOptions, setCheckFiltersOptions] = useState([]);

    useEffect(() => {
        dispatch({ type: "SITE_FULL_WIDTH" });
    }, []);

    useEffect(() => {
        if (reduxLocations && reduxLocations.length > 0) {
            setUseLocationDefaults(false);
        } else {
            setUseLocationDefaults(true);
        }
    }, [reduxLocations]);

    useEffect(() => {
        if (queryFilter && checkFiltersOptions) {
            const filter = checkFiltersOptions.find(
                (cf) => cf.key === queryFilter.toUpperCase()
            );

            if (!filter) {
                return;
            }

            let filterName = "ADDITIONALFILTERS";
            if (driversAgeKeys.includes(queryFilter)) {
                filterName = "EMPLOYEEAGE";
            } else if (pointsKeys.includes(queryFilter)) {
                filterName = "POINTS";
            } else if (enrolmentStatusKeys.includes(queryFilter)) {
                filterName = "ENROLMENT";
            }
            setAppliedFilters((prevState) => {
                return [
                    ...prevState,
                    {
                        name: filterName,
                        displayName: filter.name,
                        value: filter.key,
                    },
                ];
            });

            setDefaultFilterValues([
                {
                    name: filterName,
                    displayName: filter.name,
                    value: filter.key,
                },
            ]);
        }
    }, [queryFilter, checkFiltersOptions]);

    useEffect(() => {
        axios
            .all([
                EmployeeService.getEmployeeOptions(),
                LicenceCheckingService.getLicenceCheckingOptions(),
            ])
            .then(function ([employeeOptions, licenceCheckingOptions]) {
                setEmployeeTypeOptions(employeeOptions.employeeTypes);
                setEmployeeStatusOptions(employeeOptions.employeeStatuses);
                setCheckFiltersOptions(licenceCheckingOptions.checkFilters);
                setPageFilters([
                    {
                        name: "EMPLOYEESTATUS",
                        displayName: "Status",
                        items: employeeOptions.employeeStatuses.filter(
                            (status) => {
                                return status.active;
                            }
                        ),
                    },
                    {
                        name: "EMPLOYEETYPE",
                        displayName: "Type",
                        items: employeeOptions.employeeTypes,
                    },
                    {
                        name: "EMPLOYEECATEGORY",
                        displayName: "Category",
                        items: employeeOptions.employeeCategories,
                    },
                    {
                        name: "EMPLOYEEAGE",
                        displayName: "Employee age",
                        items: licenceCheckingOptions.checkFilters
                            .filter((cf) => driversAgeKeys.includes(cf.key))
                            .map((cf) => {
                                return { id: cf.key, name: cf.name };
                            }),
                    },
                    {
                        name: "POINTS",
                        displayName: "Points",
                        items: licenceCheckingOptions.checkFilters
                            .filter((cf) => pointsKeys.includes(cf.key))
                            .map((cf) => {
                                return { id: cf.key, name: cf.name };
                            }),
                    },
                    {
                        name: "ENROLMENT",
                        displayName: "Enrolment Status",
                        items: licenceCheckingOptions.checkFilters
                            .filter((cf) =>
                                enrolmentStatusKeys.includes(cf.key)
                            )
                            .map((cf) => {
                                return { id: cf.key, name: cf.name };
                            }),
                    },
                    {
                        name: "ADDITIONALFILTERS",
                        displayName: "Additional Filters",
                        items: licenceCheckingOptions.checkFilters
                            .filter(
                                (cf) =>
                                    !driversAgeKeys.includes(cf.key) &&
                                    !pointsKeys.includes(cf.key) &&
                                    !enrolmentStatusKeys.includes(cf.key)
                            )
                            .map((cf) => {
                                return { id: cf.key, name: cf.name };
                            }),
                    },
                ]);
                setInitalSetupComplete(true);
            });
    }, []);

    useEffect(
        function () {
            setReportList([
                <InstantReportButton
                    buttonType="MenuItem"
                    icon={<i className="fa fa-file-pdf-o"></i>}
                    key={"enrolled-employees-report"}
                    text={"Licence Checking Enrolled Employees"}
                    onReportGenerate={generateCreditReport}
                    onReportDownloadComplete={generateCreditReportComplete}
                    reportId={reportGenerationId}
                />,
            ]);

            setTimeout(() => {
                setShowReportList(false);
            });
        },
        [reportGenerationId]
    );

    useEffect(() => {
        fetchTableData(false);
    }, [
        pageNumber,
        sortedBy,
        sortedDir,
        debouncedSearchTerm,
        appliedFilters,
        searchTerm,
        initalSetupComplete,
    ]);

    function fetchTableData(forceRefresh) {
        if (!canAccessPage || !initalSetupComplete) {
            return;
        }

        if (!reduxLocations) {
            setTableRows([]);
            setTotalRecords(0);
            setIsLoading(false);
            return;
        }

        if (
            currentPageNumber.current === pageNumber &&
            currentTableSortBy.current === sortedBy &&
            currentTableSortDir.current === sortedDir &&
            currentSearchTerm.current === debouncedSearchTerm &&
            currentAppliedFilters.current === appliedFilters &&
            currentLocations.current === reduxLocations &&
            !forceRefresh
        ) {
            //If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
            return;
        }

        currentPageNumber.current = pageNumber;
        currentTableSortBy.current = sortedBy;
        currentTableSortDir.current = sortedDir;
        currentSearchTerm.current = debouncedSearchTerm;
        currentAppliedFilters.current = appliedFilters;
        currentLocations.current = reduxLocations;

        getCompletedChecks();
    }

    function getCompletedChecks() {
        setIsLoading(true);
        let searchPayload = {
            requestCount: 50,
            pageNumber: pageNumber,
            searchTerm: searchTerm,
            sortField: sortedBy.sortName,
            sortDirection: sortedDir,
            filters: [
                ...appliedFilters.map((filter) => ({
                    key: [
                        "EMPLOYEEAGE",
                        "POINTS",
                        "ENROLMENT",
                        "ADDITIONALFILTERS",
                    ].includes(filter.name)
                        ? "CHECKFILTER"
                        : filter.name,
                    value: filter.value,
                })),
                ...reduxLocations.map((locationId) => ({
                    key: "Location",
                    value: locationId,
                })),
            ],
        };

        LicenceCheckingService.getCompletedChecks(searchPayload)
            .then(
                (completedChecksResponse) => {
                    setTableRows(
                        completedChecksResponse.data.map((c) => {
                            return [
                                <div
                                    key={c.employee.id}
                                    className="inline-items"
                                >
                                    <EmployeeStatus
                                        statusId={c.employee.statusId}
                                        typeId={c.employee.typeId}
                                        typeAttributeMappings={
                                            employeeTypeOptions
                                        }
                                        statusAttributeMappings={
                                            employeeStatusOptions
                                        }
                                    />
                                    <Link
                                        to={`/employee/${c.employee.id}/licence-checking`}
                                    >
                                        {c.employee.fullName}
                                    </Link>
                                </div>,
                                c.employee.location,
                                c.employee.category,
                                c.employee.age,
                                <DateToLocal
                                    key={`photocard-expiry-${c.employee.id}`}
                                    format="DD/MM/YYYY"
                                >
                                    {c.photocardExpiryDate}
                                </DateToLocal>,
                                !c.restrictedLicence ? "No" : "Yes",
                                c.points,
                                <DateToLocal
                                    key={`entitlement-expiry-${c.employee.id}`}
                                    format="DD/MM/YYYY"
                                >
                                    {c.nextEntitlementExpiryDate}
                                </DateToLocal>,
                                !c.includedInPolicy ? 0 : c.includedInPolicy,
                                <DateToLocal
                                    key={`last-check-expiry-${c.employee.id}`}
                                    format="DD/MM/YYYY"
                                >
                                    {c.lastCheckDate}
                                </DateToLocal>,
                                <DateToLocal
                                    key={`next-check-expiry-${c.employee.id}`}
                                    format="DD/MM/YYYY"
                                >
                                    {c.nextCheckDate}
                                </DateToLocal>,
                                <DateToLocal
                                    key={`consent-expiry-${c.employee.id}`}
                                    format="DD/MM/YYYY"
                                >
                                    {c.consentExpiryDate}
                                </DateToLocal>,
                                <DateToLocal
                                    key={`renewal-available-${c.employee.id}`}
                                    format="DD/MM/YYYY"
                                >
                                    {c.renewalAvailableDate}
                                </DateToLocal>,
                                <p
                                    key={`licence-status-${c.employee.id}`}
                                    className={classNames({
                                        "form-field-red":
                                            c.licenceStatus === "Disqualified",
                                    })}
                                >
                                    {c.licenceStatus}
                                </p>,
                                <UserAccess
                                    key={`licence-checking-manual-check-${c.employee.id}`}
                                    perform={licenceCheckManualCheckAction}
                                    yes={() => (
                                        <Button
                                            id={`enrolled-employees-${c.employee.id}`}
                                            key={`enrolled-employees-${c.employee.id}`}
                                            icon="mugshot"
                                            iconOnly
                                            minimal
                                            large={false}
                                            onClick={() =>
                                                manualCheckClick(
                                                    c.employee.id,
                                                    c.employee.fullName
                                                )
                                            }
                                        />
                                    )}
                                />,
                            ];
                        })
                    );

                    setTotalRecords(completedChecksResponse.totalCount);
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Unable to load data. Please refresh the page."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Unable to load data. Please refresh the page."
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        });
    }

    function onSelectedLocationsChange(locations) {
        dispatch({
            type: "SET_LICENCE_CHECKING_LOCATIONS",
            payload: locations,
        });
    }

    function handleEmployeeListOnSort(sortField, sortDir) {
        setSortedBy(sortField);
        setSortedDir(sortDir);
    }

    function onReportDropdownClick() {
        setShowReportList((prev) => !prev);
    }

    function manualCheckClick(id, name) {
        setManualCheckEmployeeId(id);
        setManualCheckEmployeeName(name);
        setShowManualCheckModal(true);
    }

    function generateCreditReport() {
        let reportFilters = [
            ...(currentAppliedFilters.current ?? []).map((filter) => ({
                key: [
                    "EMPLOYEEAGE",
                    "POINTS",
                    "ENROLMENT",
                    "ADDITIONALFILTERS",
                ].includes(filter.name)
                    ? "CHECKFILTER"
                    : filter.name,
                value: filter.value,
            })),
            ...(currentLocations.current ?? []).map((locationId) => ({
                key: "Location",
                value: locationId,
            })),
        ];
        LicenceCheckingService.generateCompletedChecksReport(reportFilters, currentSearchTerm.current)
            .then(
                (response) => {
                    setReportGenerationId(response);
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Failed to generate report. Please try again."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Failed to generate report. Please try again."
                );
            });
    }

    function generateCreditReportComplete() {
        setReportGenerationId(null);
    }

    return (
        <>
            <div className="filter-controls">
                <div className="inline-items spacer-bottom">
                    <div className="inline-item fill">
                        <div className="form-field-full">
                            <FormTextInput
                                placeholder="Search Employees"
                                onChange={(e) => setSearchTerm(e.target.value)}
                                value={searchTerm}
                                large
                                disabled={isLoading}
                                icon="search"
                                id="enrolled-employees-search-field"
                            />
                        </div>
                    </div>
                    <div className="inline-item">
                        <FormLocationSelector
                            businessArea={"Tacho"}
                            loading={false}
                            selectedLocations={reduxLocations}
                            setSelectedLocations={onSelectedLocationsChange}
                            useLocationDefaults={useLocationDefaults}
                            useHierarchy={useHierarchy}
                            setUseHierarchy={setUseHierarchy}
                            locationDefaultsToTrue={isLoading ? null : true}
                            onLocationListClose={fetchTableData}
                        />
                    </div>                    
                    <div className="inline-item">
                        <Dropdown
                            isOpen={showReportList}
                            onClick={onReportDropdownClick}
                            intent="primary"
                            text={""}
                            icon={"document"}
                            large
                            iconOnly
                            id="reports"
                            items={reportList}
                            preamble={
                                <div>
                                    <h4>Downloads</h4>
                                    <p>
                                        Filters set on this page will be applied to
                                        the report when it is generated. Please make
                                        sure the data on the screen represents what
                                        you want to see within your report.
                                    </p>
                                </div>
                            }
                            disabled={false}
                        />
                    </div>    
                    <div className="inline-item">
                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                    </div>                
                </div>
                <Filter
                    filterName={"licenceChecking"}
                    visible={showFilters}
                    filters={pageFilters}
                    onUpdate={onFilterChange}
                    defaultFilterValues={defaultFilterValues}
                    loading={isLoading}
                    onClickOutside={onToggleFilter}
                />
            </div>
            <NotificationInline
                intent="info"
                text="Your report has been queued, and will download as soon as it's ready."
                show={reportGenerationId != null}
            />
            <ListingTable
                showPerPage={false}
                pageSize={50}
                loadingData={isLoading}
                headers={tableHeaders}
                data={tableRows}
                totalRecordCount={totalRecords}
                onPagingChange={onPagingChange}
                sortable
                sortableHeaders={sortableHeaders}
                sortedBy={sortedBy}
                sortedDir={sortedDir}
                onSort={handleEmployeeListOnSort}
            />

            <LicenceCheckingManualCheckModal
                isOpen={showManualCheckModal}
                onClose={() => {
                    setShowManualCheckModal(false);
                }}
                credits={credits}
                employeeName={manualCheckEmployeeName}
                employeeId={manualCheckEmployeeId}
                onCheckSuccess={() => {
                    fetchTableData(true);
                }}
            />
        </>
    );
}
